<template>
  <div
    :id="`email_message_activity_${activity._id || activity.id}`"
    class="transition-all gap-2"
    ref="activityContainer"
    @click="$emit('click', activity)"
  >
    <div class="custom_container" :class="{ 'cursor-pointer': !isSelected }">
      <div class="flex flex-col gap-5 p-2 text-sm w-full">
        <div class="flex justify-between pt-2 px-2 gap-2">
          <span>
            <span class="font-semibold">{{
              isReplyMessage ? "Reply received " : "Sent "
            }}</span>
            <span>{{ isReplyMessage ? "from" : "by" }}</span>
            {{ formatPerson(activity.from) }}
          </span>
          <span class="secondary_color text-[12px] text-nowrap">
            {{ formatTimestamp(activity.timestamp, "centered") }}
          </span>
        </div>
        <div class="flex flex-col px-2 gap-1">
          <div
            v-for="(field, index) in emailFields"
            :key="index"
            class="flex secondary_color gap-3 text-sm"
          >
            <span class="w-[44px]">{{ field.label }}:</span>
            <span class="flex-grow font-semibold" v-html="field.value"></span>
          </div>
        </div>

        <div v-if="isSelected" class="px-2 h-full">
          <FormatEmail
            v-if="activity.message"
            :message="activity.message"
            css="p-0"
          />
        </div>
        <div v-else class="text-sm font-semibold px-2">
          {{ truncatedMessage }}
        </div>

        <div class="flex justify-start" :class="{ 'w-full p-2': isSelected }">
          <Engagement
            :value="engagementData"
            :row="activity"
            width="w-fit"
            :show-hover-details="true"
            :timestamp-data="timestampData"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import {
  formatPerson,
  removeTrackingPixels,
  truncateEmailMessageHTML,
} from "@/src/utils/utils.js";
import { formatTimestamp } from "@/src/utils/dateTimeUtils.js";
import FormatEmail from "@/src/components/formatting_components/FormatEmail.vue";
import { ref, onMounted, watch, nextTick, computed, inject } from "vue";
import Engagement from "@/src/components/emails/renderers/Engagement.vue";
import { limitTimestampsWithEllipsis } from "@/src/utils/utils.js";

const props = defineProps({
  activity: { type: Object, required: true },
  isSelected: { type: Boolean, default: false },
  allActivities: { type: Array, default: () => [] },
});

const activityContainer = ref(null);

const allActivitiesList = inject("allActivities", props.allActivities);

const isReplyMessage = computed(() => {
  if (
    !props.activity ||
    !allActivitiesList ||
    !Array.isArray(allActivitiesList)
  ) {
    return false;
  }

  const currentMessageTimestamp = new Date(
    props.activity.timestamp,
  ).toISOString();

  // Check if this message's timestamp matches any reply timestamp from any message in the thread
  return allActivitiesList.some((activity) => {
    if (
      activity.email_reply_messages_created_at &&
      Array.isArray(activity.email_reply_messages_created_at)
    ) {
      return activity.email_reply_messages_created_at.some((replyTimestamp) => {
        const replyTime = new Date(replyTimestamp).toISOString();
        return replyTime === currentMessageTimestamp;
      });
    }
    return false;
  });
});

const getTimestampsFromBackend = computed(() => {
  const deliveredTimestamp = props.activity.timestamp;
  const openedTimestamps = props.activity.email_opened_events_created_at || [];
  const clickedTimestamps =
    props.activity.email_clicked_events_created_at || [];
  const repliedTimestamps =
    props.activity.email_reply_messages_created_at || [];

  return {
    delivered: deliveredTimestamp,
    opened: openedTimestamps,
    clicked: clickedTimestamps,
    replied: repliedTimestamps,
  };
});

const messageDeliveredTimestamp = computed(
  () => getTimestampsFromBackend.value.delivered,
);
const messageOpenedTimestamps = computed(
  () => getTimestampsFromBackend.value.opened,
);
const messageClickedTimestamps = computed(
  () => getTimestampsFromBackend.value.clicked,
);
const messageRepliedTimestamps = computed(
  () => getTimestampsFromBackend.value.replied,
);

const limitedOpenedTimestamps = computed(() => {
  const timestamps = messageOpenedTimestamps.value;
  return limitTimestampsWithEllipsis(timestamps, 5);
});

const limitedClickedTimestamps = computed(() => {
  const timestamps = messageClickedTimestamps.value;
  return limitTimestampsWithEllipsis(timestamps, 5);
});

const limitedRepliedTimestamps = computed(() => {
  const timestamps = messageRepliedTimestamps.value;
  return limitTimestampsWithEllipsis(timestamps, 5);
});

const engagementData = computed(() => {
  return {
    status: props.activity.email_stage,
    opened_count: props.activity.email_opened_count,
    clicked_count: props.activity.email_click_count,
    replied_count: props.activity.email_reply_count,
  };
});

const emailFields = computed(() => [
  {
    label: "From",
    value: `${formatPerson(props.activity.from)} (${props.activity.from.email})`,
  },
  {
    label: "To",
    value: props.activity.to
      .map((p) => formatPerson(p))
      .filter((p) => p)
      .join(", "),
  },
  {
    label: "Subject",
    value: props.activity.subject,
  },
]);

const truncatedMessage = computed(() => {
  if (!props.activity.message) return "";

  const message = removeTrackingPixels(props.activity.message);

  return truncateEmailMessageHTML(message, 70);
});

const scrollToTop = () => {
  if (activityContainer.value) {
    setTimeout(() => {
      const parentContainer = document.getElementById("email_activity");
      if (parentContainer && activityContainer.value) {
        const targetPosition = activityContainer.value.offsetTop;
        const scrollOffset = 100;
        const adjustedPosition = Math.max(0, targetPosition - scrollOffset);
        parentContainer.scrollTop = adjustedPosition;
        setTimeout(() => {
          parentContainer.scrollTo({
            top: adjustedPosition,
            behavior: "smooth",
          });
        }, 10);
      }
    }, 100);
  }
};

watch(
  () => props.isSelected,
  (newValue) => {
    if (newValue) {
      nextTick(() => {
        scrollToTop();
      });
    }
  },
);

onMounted(() => {
  if (props.isSelected) {
    nextTick(() => {
      scrollToTop();
    });
  }
});

const timestampData = computed(() => ({
  deliveredTimestamp: messageDeliveredTimestamp.value,
  openedTimestamps: limitedOpenedTimestamps.value,
  clickedTimestamps: limitedClickedTimestamps.value,
  repliedTimestamps: limitedRepliedTimestamps.value,
  originalOpenedCount: messageOpenedTimestamps.value?.length || 0,
  originalClickedCount: messageClickedTimestamps.value?.length || 0,
  originalRepliedCount: messageRepliedTimestamps.value?.length || 0,
}));
</script>
